import {
  ReactElement, useCallback, useEffect, useMemo, useState,
} from 'react';
import Skeleton from 'react-loading-skeleton';
import { WaterContainer, CardsContainerLoading } from '../styles';
import { WaterCardContent } from './CardContent';
import { WaterOverviewInfo } from '~/providers/types/api-private';
import { useToast } from '~/contexts/newToastContext';
import { useTranslation } from 'react-i18next';
import { UpgradeModule } from '~/pages/NewOverview/components/UpgradeModule';
import { getWaterOverviewInfo } from '~/pages/NewOverview/helpers/overview';
import { useFilterContext } from '~/pages/NewOverview/contexts/FilterContext';

interface WaterCardProps {
  loading: boolean;
  hasWater?: boolean;
}

export function WaterCard({
  hasWater,
  loading,
}: Readonly<WaterCardProps>): ReactElement {
  const { client, selectedOptions, filtersDependencies } = useFilterContext();
  const [loadingData, setLoadingData] = useState(false);
  const [waterData, setWaterData] = useState<WaterOverviewInfo>();
  const { toast } = useToast();
  const { t } = useTranslation();

  const generalLoading = useMemo(() => loading || loadingData, [loading, loadingData]);

  const getWaterInfo = useCallback(async (): Promise<void> => {
    setLoadingData(true);

    const data = await getWaterOverviewInfo(Number(client), {
      stateIds: selectedOptions.state,
      unitIds: selectedOptions.group?.units,
      healthStatus: selectedOptions.healthStatus,
    });

    if (data) {
      setWaterData(data);
    } else {
      toast(t('erroTitulo'), t('erroBuscaDadosAgua'), 'error');
    }
    setLoadingData(false);
  }, [filtersDependencies]);

  useEffect(() => {
    if (client && hasWater && !loading) {
      getWaterInfo();
    }
  }, [getWaterInfo, hasWater, loading, ...filtersDependencies]);

  useEffect(() => {
    if (!hasWater || loading) {
      setWaterData(undefined);
      setLoadingData(false);
    }
  }, [hasWater, loading]);

  if (generalLoading) {
    return (
      <CardsContainerLoading>
        <Skeleton
          height="360px"
          borderRadius="8px"
          className="skeleton-span"
          containerClassName="skeleton-container-span"
        />
      </CardsContainerLoading>
    );
  }

  return (
    <WaterContainer>
      {!hasWater && (<UpgradeModule module="moreModules" />)}
      <WaterCardContent data={hasWater ? waterData : undefined} />
    </WaterContainer>
  );
}
